import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { MapPin, Shield, ListChecks, Clock, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * GPSConsentModal — Iter-263
 *
 * Two usages:
 *   1. consentType="login_modal"     — back-fill modal for already-registered talent
 *                                       on next login.
 *   2. consentType="shift_check_in"  — per-shift gate at clock-in time.
 *
 * Props:
 *   open              boolean — controls visibility
 *   onAccept(consent) called after accept; receives the consent response
 *   onClose()         called when dismissed (only allowed for shift_check_in)
 *   consentType       'login_modal' | 'shift_check_in'
 *   bookingId         required for shift_check_in
 *   blocking          when true, the modal CANNOT be closed without accepting
 *
 * For registration we DO NOT use this modal — registration uses an inline
 * checkbox so the talent can read the language alongside the rest of the
 * signup form.
 */
const GPSConsentModal = ({
  open,
  onAccept,
  onClose,
  consentType = 'login_modal',
  bookingId,
  blocking = true,
}) => {
  const [policy, setPolicy] = useState(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/gps/consent/policy');
        if (!cancelled) setPolicy(res.data);
      } catch (e) {
        // Show a sensible fallback if the policy lookup fails
        if (!cancelled) {
          setPolicy({
            policy_version: 'v1',
            language: 'By accepting, you consent to GPS attendance verification while clocked in.',
            purposes: [
              'Attendance verification',
              'Shift compliance',
              'Fraud prevention',
              'Payment validation',
              'Safety and operational support',
            ],
            collection_window: 'Only while clocked in to an active shift.',
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handleAccept = async () => {
    if (!checked) return;
    setSubmitting(true);
    try {
      const res = await api.post('/gps/consent/accept', {
        consent_type: consentType,
        booking_id: bookingId,
        policy_version: policy?.policy_version,
      });
      toast.success('Consent recorded — thank you');
      if (onAccept) onAccept(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record consent');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (next) => {
    if (next) return;
    if (blocking) return; // refuse to close
    if (onClose) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="bg-white max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="gps-consent-modal"
        onPointerDownOutside={(e) => blocking && e.preventDefault()}
        onEscapeKeyDown={(e) => blocking && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Shield className="w-5 h-5 text-cyan-600" />
            GPS Attendance Verification Consent
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {consentType === 'shift_check_in'
              ? 'Required before you can check in to this shift.'
              : 'We need your consent to continue using attendance verification.'}
          </DialogDescription>
        </DialogHeader>

        {!policy ? (
          <div className="py-6 flex items-center justify-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading policy…
          </div>
        ) : (
          <div className="space-y-4 text-sm text-slate-700">
            <p className="leading-relaxed">{policy.language}</p>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
              <div className="flex items-start gap-2">
                <ListChecks className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-slate-900">Why we collect this</div>
                  <ul className="list-disc list-inside text-xs text-slate-600 space-y-0.5 mt-1">
                    {policy.purposes?.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-slate-900">When we collect it</div>
                  <p className="text-xs text-slate-600 mt-0.5">{policy.collection_window}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-slate-900">What you&apos;ll see</div>
                  <p className="text-xs text-slate-600 mt-0.5">
                    A persistent &ldquo;GPS Tracking Active for Attendance Verification&rdquo; banner during
                    your active shift. Tracking stops automatically when you clock out.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-slate-900">If GPS is disabled mid-shift</div>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Your shift will be flagged for admin review and Instant Pay will be paused
                    until the review is complete.
                  </p>
                </div>
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer select-none rounded-lg border border-slate-200 p-3 hover:bg-slate-50">
              <Checkbox
                checked={checked}
                onCheckedChange={(v) => setChecked(!!v)}
                data-testid="gps-consent-checkbox"
                className="mt-0.5"
              />
              <span className="text-sm text-slate-700">
                <span className="font-semibold text-slate-900">I accept the GPS Attendance Verification policy</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Policy version <span className="font-mono">{policy.policy_version}</span> · Accepted on <span>{new Date().toLocaleDateString()}</span>
                </span>
              </span>
            </label>
          </div>
        )}

        <DialogFooter className="gap-2">
          {!blocking && (
            <Button
              variant="ghost"
              onClick={onClose}
              data-testid="gps-consent-cancel-btn"
              disabled={submitting}
            >
              Not now
            </Button>
          )}
          <Button
            onClick={handleAccept}
            disabled={!checked || submitting || !policy}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="gps-consent-accept-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Accept & Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GPSConsentModal;
